import { useCallback, type ReactNode } from 'react'
import { LetterGlasGroup, LetterGlasRail } from '@/components/ui/LetterGlasRail'
import { useLetterGlas } from '@/hooks/useLetterGlas'

export function LetterGlasDirectory<T>({
  items,
  getText,
  getKey,
  selectedKey,
  onSelect,
  query,
  onQueryChange,
  placeholder = 'Suchen…',
  empty = 'Keine Treffer',
  ariaLabelPrefix,
  renderRow,
}: {
  items: T[]
  getText: (item: T) => string
  getKey: (item: T) => string
  selectedKey?: string | null
  onSelect: (item: T) => void
  query?: string
  onQueryChange?: (q: string) => void
  placeholder?: string
  empty?: string
  ariaLabelPrefix?: string
  renderRow: (item: T) => ReactNode
}) {
  const textOf = useCallback((item: T) => getText(item), [getText])
  const {
    letter,
    setLetter,
    hoverLetter,
    setHoverLetter,
    focusLetter,
    have,
    letters,
    grouped,
  } = useLetterGlas(items, textOf)

  return (
    <>
      {onQueryChange ? (
        <input
          className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] focus:border-blue-600 focus:outline-none focus:shadow-[0_0_0_3px_#dbeafe]"
          placeholder={placeholder}
          value={query ?? ''}
          onChange={e => onQueryChange(e.target.value)}
        />
      ) : null}
      <div className={`${onQueryChange ? 'mt-2.5 ' : ''}flex max-h-[480px] items-stretch gap-4`}>
        <LetterGlasRail
          letters={letters}
          have={have}
          letter={letter}
          hoverLetter={hoverLetter}
          focusLetter={focusLetter}
          ariaLabelPrefix={ariaLabelPrefix}
          onHover={setHoverLetter}
          onPick={setLetter}
        />
        <div className="min-w-0 flex-1 overflow-auto">
          {grouped.length === 0 ? (
            <div className="px-1 py-7 text-center text-[13px] text-gray-400">{empty}</div>
          ) : grouped.map(([L, rows]) => {
            const out = !!focusLetter && focusLetter !== L
            return (
              <LetterGlasGroup key={L} letter={L} hidden={out}>
                {rows.map(item => {
                  const key = getKey(item)
                  return (
                    <button
                      key={key}
                      type="button"
                      tabIndex={out ? -1 : undefined}
                      className={`flex w-full items-center gap-2.5 border-b border-gray-100 px-1.5 py-2 text-left ${selectedKey === key ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                      onClick={() => { if (!out) onSelect(item) }}
                    >
                      {renderRow(item)}
                    </button>
                  )
                })}
              </LetterGlasGroup>
            )
          })}
        </div>
      </div>
    </>
  )
}
